import { useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import dayjs from 'dayjs';
import { useAuth } from '@frontegg/react';
import { isInSaaSMode } from '../../utils/common';
import usePosthogSessionInfo from './usePosthogSessionInfo';

interface ApiErrorContext {
  endpoint?: string;
  method?: string;
  statusCode?: number;
  responseBody?: string;
  requestPayload?: unknown;
}

interface UseViewErrorsTrackingProps {
  error?: Error;
  errorName?: 'ApiError' | 'ReactRenderingError';
  context?: {
    component:
      | 'PanelError'
      | 'FrontendErrorPage'
      | 'ReportErrorPage'
      | 'CreateJiraTicketError'
      | 'CreateLinearTicketError';
    page?: string;
    api?: ApiErrorContext;
    previousRoute?: string;
    componentProps?: Record<string, unknown>;
    appState?: Record<string, unknown>;
    sessionDuration?: number;
    tags?: string[];
    [key: string]: unknown;
  };
}

const useViewErrorsTracking = ({
  error,
  errorName = 'ReactRenderingError',
  context,
}: UseViewErrorsTrackingProps) => {
  const { user } = useAuth();
  const isSaaS = isInSaaSMode();

  const { sessionId: posthogSessionId, sessionUrl: posthogSessionUrl } =
    usePosthogSessionInfo();

  useEffect(() => {
    if (error && isSaaS) {
      const trackedError = new Error(error.message);
      trackedError.name = errorName || error.name || 'Error';
      trackedError.stack = error.stack;

      const enrichedContext: Record<string, any> = {
        ...context,

        currentPath: window.location.pathname,
        queryParams: window.location.search,
        hash: window.location.hash,

        timestamp: dayjs(new Date(), 'YYYY-MM-DD HH:mm:ss').toString(),

        isOnline: navigator.onLine,
        connectionType: (navigator as any).connection?.effectiveType,

        errorMessage: error.message,
      };

      if (posthogSessionId) {
        enrichedContext.posthogSessionId = posthogSessionId;
      }
      if (posthogSessionUrl) {
        enrichedContext.posthogSessionUrl = posthogSessionUrl;
      }

      enrichedContext.isImpersonated = !!user?.act;

      const perfMemory = (performance as any).memory;
      if (perfMemory) {
        enrichedContext.memoryUsage = {
          used: Math.round(perfMemory.usedJSHeapSize / 1048576),
          total: Math.round(perfMemory.totalJSHeapSize / 1048576),
        };
      }

      if (error.cause) {
        enrichedContext.errorCause = String(error.cause);
      }

      // Extract any custom properties from the error object
      Object.keys(error).forEach((key) => {
        if (!['message', 'name', 'stack', 'cause'].includes(key)) {
          enrichedContext[`error_${key}`] = (error as any)[key];
        }
      });

      datadogRum.addError(trackedError, enrichedContext);
    }
  }, [
    error,
    errorName,
    context,
    isSaaS,
    posthogSessionId,
    posthogSessionUrl,
    user?.act,
  ]);
};

export default useViewErrorsTracking;
